import { useState } from 'react';

/**
 * Custom hook for AI Fluency Diagnostic API integration
 * Handles assessment start, submission, and error states
 */
const useDiagnostic = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_DIAGNOSTIC_API || 
    'https://aztese0ikh.execute-api.us-east-1.amazonaws.com/prod';

  /**
   * Start a new assessment
   * @param {Object} userData - { email, full_name, job_title }
   * @returns {Promise<Object>} { result_id, questions[] }
   */
  const startAssessment = async (userData) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/assessment/start`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          full_name: userData.full_name,
          job_title: userData.job_title
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API error: ${response.status} - ${errorText || response.statusText}`);
      }

      const data = await response.json();
      setLoading(false);
      return data;
    } catch (err) {
      console.error('Error in startAssessment:', err);
      // More detailed error message for debugging
      const errorMsg = err.message || err.toString() || 'Network error - check connection';
      setError(errorMsg);
      setLoading(false);
      throw new Error(errorMsg);
    }
  };

  /**
   * Submit completed assessment
   * @param {Object} submissionData - { result_id, answers, completion_time_seconds }
   * @returns {Promise<Object>} { total_score, fluency_level, competency_scores, report_url }
   */
  const submitAssessment = async (submissionData) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/assessment/submit`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          result_id: submissionData.result_id,
          answers: submissionData.answers,
          completion_time_seconds: submissionData.completion_time_seconds || 0
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setLoading(false);
      return data;
    } catch (err) {
      console.error('Error in submitAssessment:', err);
      setError(err.message || 'Failed to submit assessment. Please try again.');
      setLoading(false);
      throw err;
    }
  };

  return {
    startAssessment,
    submitAssessment,
    loading,
    error
  };
};

export default useDiagnostic;
